import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import { Download, Images, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, euro, imgSrc, GENDERS, CATEGORIES } from "@/lib/api";
import { renderSlideCanvas, loadImg, computeCoverZoom, slideFileName } from "@/lib/slideRenderer";

const money = (n) => (n == null || n === "" ? "" : Number(n).toFixed(2).replace(".", ",").replace(",00", "") + "€");
const GENDER_LABEL = Object.fromEntries(GENDERS.map((g) => [g.id, g.label.toUpperCase()]));
const sizeSort = (a, b) => (parseFloat(a.size) || 999) - (parseFloat(b.size) || 999) || String(a.size).localeCompare(String(b.size));

// One slide per product+colour (default) or one per product with all colours merged.
export const specsFromProduct = (p, perProduct = false) => {
  const inStock = (p.sizes || []).filter((s) => s.stock > 0);
  const colors = perProduct ? [null] : [...new Set(inStock.map((s) => s.color || ""))];
  const cp = p.compare_price;
  const onSale = cp && cp > p.price;
  const brand = p.brand || "";
  let name = p.name || "";
  if (brand && name.toLowerCase().startsWith(brand.toLowerCase())) name = name.slice(brand.length).trim().replace(/^[-·]\s*/, "");
  const mergedSizes = () => {
    const acc = new Map();
    inStock.forEach((s) => acc.set(s.size, (acc.get(s.size) || 0) + s.stock));
    return [...acc.entries()].map(([size, qty]) => ({ size, qty: String(qty) })).sort(sizeSort);
  };
  return colors.map((color) => ({
    key: color === null ? `${p.id}|*` : `${p.id}|${color}`,
    productId: p.id,
    category: GENDER_LABEL[p.gender] || "",
    brand,
    productName: name,
    color: color || "",
    price: money(onSale ? cp : p.price),
    showPrice: true,
    offerPrice: onSale ? money(p.price) : "",
    showOffer: !!onSale,
    showSizes: true,
    sizesLabel: p.category === "calzado" ? "NÚMEROS" : "TALLAS",
    sizes: color === null ? mergedSizes() : inStock.filter((s) => (s.color || "") === color).sort(sizeSort).map((s) => ({ qty: String(s.stock), size: s.size })),
    foto: (color && ((p.color_images || {})[color] || [])[0]) || (p.images || [])[0] || "",
    zoom: 0,
    offX: 0,
    offY: 0,
  }));
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span>{children}</label>
);

export const SlidesTab = () => {
  const call = useApi();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("all");
  const [gender, setGender] = useState("all");
  const [cat, setCat] = useState("all");
  const [picked, setPicked] = useState(() => new Set());
  const [specs, setSpecs] = useState([]);
  const [perProduct, setPerProduct] = useState(false);
  const [zipping, setZipping] = useState(0);
  const photoCache = useRef(new Map());

  useEffect(() => { call("get", "/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e))); }, [call]);

  const candidates = useMemo(() => products.filter((p) => p.active && p.sizes.some((s) => s.stock > 0)
    && (section === "all" || (section === "outlet") === !!p.outlet)
    && (gender === "all" || p.gender === gender) && (cat === "all" || p.category === cat)
    && (!q || `${p.name} ${p.brand} ${p.reference || ""}`.toLowerCase().includes(q.toLowerCase()))), [products, section, gender, cat, q]);

  const addProducts = (list) => {
    setSpecs((cur) => {
      const have = new Set(cur.map((s) => s.key));
      const add = list.flatMap((p) => specsFromProduct(p, perProduct)).filter((s) => !have.has(s.key));
      if (!add.length) toast.info("Esos artículos ya están en la lista");
      else toast.success(`${add.length} slide${add.length > 1 ? "s" : ""} añadida${add.length > 1 ? "s" : ""}`);
      return [...cur, ...add];
    });
    setPicked(new Set());
  };

  // Photos go through our backend (same origin / admin proxy) so the canvas never gets tainted.
  const getPhoto = useCallback(async (url) => {
    if (!url) return null;
    if (photoCache.current.has(url)) return photoCache.current.get(url);
    let objectUrl = null;
    try {
      const src = url.startsWith("/") ? imgSrc(url) : null;
      const { data } = src
        ? await call("get", src.replace(/^.*\/api/, ""), undefined, { responseType: "blob" })
        : await call("get", "/admin/photo-proxy", undefined, { params: { url }, responseType: "blob" });
      objectUrl = URL.createObjectURL(data);
    } catch { objectUrl = null; }
    const img = objectUrl ? await loadImg(objectUrl) : null;
    photoCache.current.set(url, img);
    return img;
  }, [call]);

  const renderSpec = useCallback(async (s, slideNumber) => {
    const logo = await loadImg("/logo-slides.png");
    const photo = await getPhoto(s.foto);
    const zoom = s.zoom || (photo ? computeCoverZoom(photo) : 100);
    return renderSlideCanvas({ ...s, zoom, slideNumber }, photo, logo);
  }, [getPhoto]);

  const update = (key, patch) => setSpecs((cur) => cur.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const remove = (key) => setSpecs((cur) => cur.filter((s) => s.key !== key));

  const downloadOne = async (s) => {
    const canvas = await renderSpec(s);
    const a = Object.assign(document.createElement("a"), { href: canvas.toDataURL("image/png"), download: slideFileName(s) });
    a.click();
  };

  const downloadAll = async () => {
    if (!specs.length) return;
    setZipping(1);
    try {
      const zip = new JSZip();
      for (let i = 0; i < specs.length; i++) {
        const canvas = await renderSpec(specs[i], i + 1);
        zip.file(`${String(i + 1).padStart(2, "0")}_${slideFileName(specs[i])}`, canvas.toDataURL("image/png").split(",")[1], { base64: true });
        setZipping(i + 1);
        await new Promise((r) => setTimeout(r, 0));
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `sobrino_slides_${new Date().toISOString().slice(0, 10)}.zip` });
      a.click();
      toast.success(`${specs.length} slides descargadas en ZIP`);
    } catch (e) { toast.error("No se pudo generar el ZIP"); }
    finally { setZipping(0); }
  };

  const sel = "h-9 border rounded-full px-3 text-sm bg-white";
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6" data-testid="admin-slides-tab">
      <div className="bg-white border p-4 space-y-3 self-start">
        <p className="filter-title flex items-center gap-2"><Images className="w-4 h-4" /> Elegir artículos</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, marca o referencia" className="field !h-9" data-testid="slides-search" />
        <div className="flex flex-wrap gap-2">
          <select value={section} onChange={(e) => setSection(e.target.value)} className={sel} data-testid="slides-section"><option value="all">Web y outlet</option><option value="shop">Solo web</option><option value="outlet">Solo outlet</option></select>
          <select value={gender} onChange={(e) => setGender(e.target.value)} className={sel} data-testid="slides-gender"><option value="all">Todos</option>{GENDERS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={sel} data-testid="slides-category"><option value="all">Todas las secciones</option>{CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
        </div>
        <div className="flex gap-1 text-xs font-semibold" data-testid="slides-mode">
          {[["color", "Una slide por color"], ["product", "Una por artículo (colores juntos)"]].map(([v, l]) => (
            <button key={v} onClick={() => setPerProduct(v === "product")} className={`flex-1 h-8 border ${(v === "product") === perProduct ? "bg-brand text-white border-brand" : "bg-white hover:border-slate-400"}`} data-testid={`slides-mode-${v}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button className="btn-primary !h-9 flex-1" disabled={!picked.size} onClick={() => addProducts(candidates.filter((p) => picked.has(p.id)))} data-testid="slides-add-picked"><Plus className="w-4 h-4 mr-1" /> Añadir ({picked.size})</button>
          <button className="btn-outline !h-9 flex-1" disabled={!candidates.length} onClick={() => addProducts(candidates)} data-testid="slides-add-all">Añadir todos ({candidates.length})</button>
        </div>
        <ul className="divide-y max-h-[60vh] overflow-y-auto text-sm" data-testid="slides-candidates">
          {candidates.map((p) => (
            <li key={p.id} className="py-2 flex items-center gap-2">
              <input type="checkbox" checked={picked.has(p.id)} onChange={() => setPicked((s) => { const c = new Set(s); c.has(p.id) ? c.delete(p.id) : c.add(p.id); return c; })} className="w-4 h-4 accent-[#0D1F7A]" data-testid={`slides-pick-${p.id}`} />
              <img src={imgSrc(p.images[0], 200)} alt="" className="w-8 h-10 object-cover bg-slate-100" loading="lazy" />
              <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{p.name}</span><span className="block text-xs text-slate-500">{p.brand} · {euro(p.price)}{p.outlet ? " · Outlet" : ""}</span></span>
            </li>
          ))}
          {!candidates.length && <li className="py-6 text-center text-slate-500">Sin artículos con stock para ese filtro</li>}
        </ul>
      </div>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="filter-title mr-auto">Slides ({specs.length})</p>
          {specs.length > 0 && <button className="btn-outline !h-9" onClick={() => setSpecs([])} data-testid="slides-clear"><X className="w-4 h-4 mr-1" /> Vaciar</button>}
          <button className="btn-primary !h-9 !bg-verde hover:!bg-verde/90" disabled={!specs.length || !!zipping} onClick={downloadAll} data-testid="slides-download-all">
            <Download className="w-4 h-4 mr-1" /> {zipping ? `Generando ${zipping}/${specs.length}…` : "Descargar todas (ZIP)"}
          </button>
        </div>
        {!specs.length && <div className="bg-white border p-10 text-center text-slate-500" data-testid="slides-empty">Elige artículos a la izquierda y pulsa "Añadir". Cada color con stock genera una slide con sus tallas, PVP y oferta.</div>}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {specs.map((s, i) => <SlideCard key={s.key} spec={s} index={i + 1} render={renderSpec} onChange={(patch) => update(s.key, patch)} onRemove={() => remove(s.key)} onDownload={() => downloadOne(s)} />)}
        </div>
      </div>
    </div>
  );
};

const SlideCard = ({ spec, index, render, onChange, onRemove, onDownload }) => {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      const c = await render(spec);
      if (!alive) return;
      const small = document.createElement("canvas");
      small.width = 432; small.height = 480;
      small.getContext("2d").drawImage(c, 0, 0, 432, 480);
      setPreview(small.toDataURL("image/jpeg", 0.85));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [spec, render]);
  const setSize = (k, patch) => onChange({ sizes: spec.sizes.map((x, j) => (j === k ? { ...x, ...patch } : x)) });
  const inp = "field !h-8 !text-sm";
  return (
    <div className="bg-white border p-3 space-y-2" data-testid={`slide-card-${index}`}>
      <div className="relative">
        {preview ? <img src={preview} alt="" className="w-full aspect-[9/10] object-cover bg-slate-100" data-testid={`slide-preview-${index}`} /> : <div className="w-full aspect-[9/10] bg-slate-100 animate-pulse" />}
        <span className="absolute top-2 left-2 bg-amber-400 text-brand text-xs font-black px-2 py-0.5">#{index}</span>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary !h-8 flex-1 !text-xs" onClick={onDownload} data-testid={`slide-download-${index}`}><Download className="w-3.5 h-3.5 mr-1" /> PNG</button>
        <button className="btn-outline !h-8 !text-xs" onClick={() => setOpen(!open)} data-testid={`slide-edit-${index}`}>{open ? "Cerrar" : "Editar"}</button>
        <button className="icon-btn !h-8 !w-8 text-rose-600" onClick={onRemove} data-testid={`slide-remove-${index}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Field label="Etiqueta superior"><input className={inp} value={spec.category} onChange={(e) => onChange({ category: e.target.value })} /></Field>
          <Field label="Marca"><input className={inp} value={spec.brand} onChange={(e) => onChange({ brand: e.target.value })} /></Field>
          <Field label="Producto" className="col-span-2"><input className={inp} value={spec.productName} onChange={(e) => onChange({ productName: e.target.value })} /></Field>
          <Field label="PVP"><div className="flex items-center gap-2"><input className={inp} value={spec.price} onChange={(e) => onChange({ price: e.target.value })} /><input type="checkbox" checked={spec.showPrice} onChange={(e) => onChange({ showPrice: e.target.checked })} title="Mostrar" /></div></Field>
          <Field label="Oferta"><div className="flex items-center gap-2"><input className={inp} value={spec.offerPrice} onChange={(e) => onChange({ offerPrice: e.target.value })} /><input type="checkbox" checked={spec.showOffer} onChange={(e) => onChange({ showOffer: e.target.checked })} title="Mostrar" /></div></Field>
          <Field label="Título tallas"><input className={inp} value={spec.sizesLabel} onChange={(e) => onChange({ sizesLabel: e.target.value })} /></Field>
          <Field label="Zoom foto"><input type="range" min="100" max="400" value={spec.zoom || 100} onChange={(e) => onChange({ zoom: Number(e.target.value) })} className="w-full accent-[#0D1F7A]" /></Field>
          <Field label="Mover foto" className="col-span-2">
            <div className="flex items-center gap-1" data-testid={`slide-move-${index}`}>
              {[["←", { offX: spec.offX - 20 }], ["→", { offX: spec.offX + 20 }], ["↑", { offY: spec.offY - 20 }], ["↓", { offY: spec.offY + 20 }]].map(([t, patch]) => (
                <button key={t} className="btn-outline !h-8 !w-9 !px-0 font-black" onClick={() => onChange(patch)} data-testid={`slide-move-${index}-${t}`}>{t}</button>
              ))}
              <button className="btn-outline !h-8 !text-xs ml-1" onClick={() => onChange({ offX: 0, offY: 0, zoom: 0 })} data-testid={`slide-center-${index}`}>Centrar</button>
              <span className="text-[11px] text-slate-400 ml-auto">{spec.offX},{spec.offY}</span>
            </div>
          </Field>
          <div className="col-span-2 space-y-1">
            <div className="flex items-center justify-between"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tallas (uds - talla)</span><label className="text-xs flex items-center gap-1"><input type="checkbox" checked={spec.showSizes} onChange={(e) => onChange({ showSizes: e.target.checked })} /> Mostrar</label></div>
            {spec.sizes.map((s, k) => (
              <div key={k} className="flex gap-1">
                <input className={`${inp} !w-14`} value={s.qty} onChange={(e) => setSize(k, { qty: e.target.value })} />
                <input className={inp} value={s.size} onChange={(e) => setSize(k, { size: e.target.value })} />
                <button className="icon-btn !h-8 !w-8" onClick={() => onChange({ sizes: spec.sizes.filter((_, j) => j !== k) })}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button className="text-xs font-semibold text-brand underline" onClick={() => onChange({ sizes: [...spec.sizes, { qty: "1", size: "" }] })}>+ Añadir talla</button>
          </div>
        </div>
      )}
    </div>
  );
};
